// Term browsing: header search dropdown, the Terms dialog (public browser /
// admin manager with full CRUD + synonyms), and the term detail dialog with
// jump-to-appearance links.
import type { TermSummary } from '../shared/types';
import { api, ApiError } from './api';
import { loadPayload } from './data';
import { $, debounce, el } from './dom';
import { fmtMs } from './format';
import { gotoFrameMs } from './frames';
import { state } from './state';
import { toast } from './toast';

export function initTermsUi(): void {
  initHeaderSearch();
  initTermsDialog();
}

export function closeAllDialogs(): void {
  document.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach((d) => d.close());
}

// ------------------------------------------------------- header search

function initHeaderSearch(): void {
  const input = $<HTMLInputElement>('#termSearch');
  const results = $<HTMLElement>('#searchResults');

  const run = debounce(async () => {
    const q = input.value.trim();
    if (!q) {
      results.hidden = true;
      return;
    }
    let terms: TermSummary[];
    try {
      terms = await api.searchTerms(q);
    } catch {
      return;
    }
    results.textContent = '';
    for (const term of terms.slice(0, 12)) {
      const btn = el('button');
      btn.appendChild(el('span', '', term.text));
      if (term.synonyms.length) btn.appendChild(el('span', 'syn', ` — ${term.synonyms.join(', ')}`));
      btn.appendChild(el('span', 'syn', ` · used ${term.uses}×`));
      btn.addEventListener('click', () => {
        results.hidden = true;
        void openTermDetail(term.id);
      });
      results.appendChild(btn);
    }
    if (terms.length === 0) results.appendChild(el('button', 'syn', 'no matches'));
    results.hidden = false;
  }, 200);

  input.addEventListener('input', () => void run());
  input.addEventListener('focus', () => {
    if (input.value.trim()) void run();
  });
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Node) || !$('.search-wrap').contains(e.target)) results.hidden = true;
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') results.hidden = true;
  });
}

// ------------------------------------------------------- terms dialog

function initTermsDialog(): void {
  const dialog = $<HTMLDialogElement>('#termsDialog');
  const filter = $<HTMLInputElement>('#termsDialogSearch');

  $('#btnTerms').addEventListener('click', () => {
    filter.value = '';
    dialog.showModal();
    void renderTermsList();
  });
  filter.addEventListener('input', debounce(() => void renderTermsList(), 200));

  $<HTMLFormElement>('#termCreateForm').addEventListener('submit', (e) => {
    e.preventDefault();
    const textInput = $<HTMLInputElement>('#newTermText');
    const synInput = $<HTMLInputElement>('#newTermSynonyms');
    const text = textInput.value.trim();
    if (!text) return;
    const synonyms = synInput.value.split(',').map((s) => s.trim()).filter(Boolean);
    api.admin
      .createTerm(text, synonyms)
      .then(() => {
        textInput.value = '';
        synInput.value = '';
        toast(`Term “${text}” created`, 'ok');
        void renderTermsList();
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'create failed', 'err'));
  });
}

async function renderTermsList(): Promise<void> {
  const q = $<HTMLInputElement>('#termsDialogSearch').value.trim();
  const list = $('#termsList');
  let terms: TermSummary[];
  try {
    terms = state.admin ? await api.admin.searchTerms(q) : await api.searchTerms(q);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'loading terms failed', 'err');
    return;
  }
  list.textContent = '';
  if (terms.length === 0) {
    list.appendChild(el('li', 'dim', q ? 'No matching terms.' : 'No terms yet.'));
    return;
  }
  for (const term of terms) list.appendChild(state.admin ? adminTermRow(term) : publicTermRow(term));
}

function publicTermRow(term: TermSummary): HTMLLIElement {
  const li = el('li');
  const name = el('button', 'term-name', term.text);
  name.addEventListener('click', () => void openTermDetail(term.id));
  li.appendChild(name);
  li.appendChild(el('span', 'syns', term.synonyms.join(', ')));
  li.appendChild(el('span', 'uses', `used ${term.uses}×`));
  return li;
}

function adminTermRow(term: TermSummary): HTMLLIElement {
  const li = el('li');
  const name = el('button', 'term-name', term.text);
  name.title = term.status === 'pending' ? 'Pending term (not yet public)' : 'Show details';
  name.addEventListener('click', () => {
    if (term.status === 'pending') toast('This term is pending — approve one of its suggestions first.', 'info');
    else void openTermDetail(term.id);
  });
  li.appendChild(name);
  if (term.status === 'pending') li.appendChild(el('span', 'chip pending', 'pending'));

  const syns = el('span', 'syns');
  // Admin synonym chips need ids — fetch lazily per action instead; here we
  // show the text list with removal via the ✕ on each chip.
  for (const syn of term.synonyms) {
    const chip = el('span', 'syn-chip', syn);
    const remove = el('button', '', '✕');
    remove.title = `Remove synonym “${syn}”`;
    remove.addEventListener('click', () => void removeSynonymByText(term, syn));
    chip.appendChild(remove);
    syns.appendChild(chip);
  }
  li.appendChild(syns);
  li.appendChild(el('span', 'uses', `used ${term.uses}×`));

  const actions = el('span', 'row-actions');
  const addSyn = el('button', 'ghost', '+syn');
  addSyn.title = 'Add a synonym';
  addSyn.addEventListener('click', () => {
    const text = prompt(`New synonym for “${term.text}”:`);
    if (!text || !text.trim()) return;
    api.admin
      .addSynonym(term.id, text.trim())
      .then(() => renderTermsList())
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'failed', 'err'));
  });
  const rename = el('button', 'ghost', 'rename');
  rename.addEventListener('click', () => {
    const text = prompt('New text for this term:', term.text);
    if (!text || !text.trim() || text.trim() === term.text) return;
    api.admin
      .renameTerm(term.id, text.trim())
      .then(() => renderTermsList())
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'failed', 'err'));
  });
  const del = el('button', 'ghost danger', 'delete');
  del.addEventListener('click', () => {
    if (!confirm(`Delete term “${term.text}”? This removes all its polygon connections and votes.`)) return;
    api.admin
      .deleteTerm(term.id)
      .then(async () => {
        toast('Term deleted', 'ok');
        await renderTermsList();
        void loadPayload();
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'failed', 'err'));
  });
  actions.append(addSyn, rename, del);
  li.appendChild(actions);
  return li;
}

async function removeSynonymByText(term: TermSummary, syn: string): Promise<void> {
  if (!confirm(`Remove synonym “${syn}” from “${term.text}”?`)) return;
  try {
    await api.admin.deleteSynonymByText(term.id, syn);
    await renderTermsList();
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'failed', 'err');
  }
}

// ------------------------------------------------------- term detail

export async function openTermDetail(id: number): Promise<void> {
  let detail;
  try {
    detail = await api.termDetail(id);
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'failed to load term', 'err');
    return;
  }
  $('#termDetailTitle').textContent = detail.text;
  const body = $('#termDetailBody');
  body.textContent = '';

  if (detail.synonyms.length) {
    const p = el('p');
    p.appendChild(el('span', 'dim', 'Synonyms: '));
    p.appendChild(document.createTextNode(detail.synonyms.join(', ')));
    body.appendChild(p);
  }
  body.appendChild(el('p', 'dim', `Connected to ${detail.appearances.length} polygon${detail.appearances.length === 1 ? '' : 's'}.`));

  if (detail.appearances.length) {
    const list = el('ul', 'terms-list compact');
    for (const app of detail.appearances) {
      const li = el('li');
      const jump = el('button', 'term-name', `${fmtMs(app.frame_ms)} · polygon #${app.polygon_id}`);
      jump.addEventListener('click', () => {
        closeAllDialogs();
        state.selectedPolygonId = app.polygon_id; // retained by the payload loader
        gotoFrameMs(app.frame_ms);
      });
      li.appendChild(jump);
      list.appendChild(li);
    }
    body.appendChild(list);
  }
  $<HTMLDialogElement>('#termDetailDialog').showModal();
}
